import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/axiosConfig";
import Navbar from "./Navbar";
import ChatTabs from "./ChatTabs";

const ChatList = () => {
  const { user: authUser, token, notifications, setNotifications, unreadNotificationCount, setUnreadNotificationCount } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Đã nhận");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Tìm kiếm người dùng
  const [searchUserQuery, setSearchUserQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Tìm kiếm tin nhắn
  const [searchMessageQuery, setSearchMessageQuery] = useState("");
  const [searchMessages, setSearchMessages] = useState([]);
  const canSearchMessages = authUser && ["vip_gold", "vip_platinum", "vip_diamond", "admin", "mod"].includes(authUser.role);

  // Hàm lấy thông tin người dùng
  const getUserInfo = useCallback(
    (userId) => {
      const user = users.find((u) => String(u._id) === String(userId));
      if (!user) {
        console.log(`Không tìm thấy người dùng với ID ${userId} trong danh sách users.`);
        return { _id: userId, name: "Không xác định", role: "user", primaryPhoto: { photoUrl: "" }, avatar: "", isOnline: false };
      }
      return { ...user, role: user?.role || "user" };
    },
    [users]
  );

  // Hàm lấy URL ảnh đại diện
  const getAvatarUrl = (user) => {
  
    return user?.primaryPhoto?.photoUrl || user?.avatar || "https://placehold.co/50";
  };

  // Lấy danh sách người dùng chat
  useEffect(() => {
    if (!authUser || !authUser._id || !token) return;

    const fetchChatUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/api/chat/chat-participants-with-unread", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { participants: fetchedUsers, unreadCounts } = res.data;

        console.log("Danh sách người dùng từ API:", fetchedUsers);

        fetchedUsers.forEach((user) => {
          if (user.isOnline === undefined) {
            console.warn(`Người dùng ${user._id} thiếu trường 'isOnline'.`);
          }
          if (!user.primaryPhoto && !user.avatar) {
            console.warn(`Người dùng ${user._id} thiếu các trường ảnh đại diện (primaryPhoto, avatar).`, user);
          }
        });

        let filteredUsers = fetchedUsers;
        if (activeTab === "Yêu thích") {
          filteredUsers = fetchedUsers.filter((user) =>
            authUser.friends?.includes(user._id)
          );
        } else if (activeTab === "Đã gửi") {
          filteredUsers = fetchedUsers.filter(
            (user) => user.latestMessageTimestamp && user.senderId === authUser._id
          );
        } else if (activeTab === "Thường xuyên") {
          filteredUsers = fetchedUsers;
        }

        const sortedUsers = filteredUsers.sort((a, b) => {
          const unreadDiff = (unreadCounts[b._id] || 0) - (unreadCounts[a._id] || 0);
          if (unreadDiff !== 0) return unreadDiff;
          return new Date(b.latestMessageTimestamp) - new Date(a.latestMessageTimestamp);
        });

        setUsers(sortedUsers);
        setUnreadCounts(unreadCounts);
        setCurrentPage(1); // Reset về trang 1 khi danh sách thay đổi
      } catch (err) {
        console.error("Lỗi khi lấy danh sách người dùng chat:", err.response?.data || err.message);
        setError("Không thể tải danh sách cuộc trò chuyện: " + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchChatUsers();
  }, [authUser, token, activeTab]);

  // Lọc người dùng dựa trên từ khóa tìm kiếm
  useEffect(() => {
    if (!searchUserQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) =>
        user.name.toLowerCase().includes(searchUserQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
    setCurrentPage(1); // Reset trang về 1 khi tìm kiếm
  }, [users, searchUserQuery]);

  // Tìm kiếm tin nhắn
  useEffect(() => {
    if (!canSearchMessages || !searchMessageQuery.trim()) {
      setSearchMessages([]);
      return;
    }

    const fetchSearchMessages = async () => {
      try {
        const res = await api.get("/api/chat/search-messages", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            query: searchMessageQuery,
          },
        });
        const { messages } = res.data;
        setSearchMessages(messages);
      } catch (err) {
        console.error("Lỗi khi tìm kiếm tin nhắn:", err.response?.data || err.message);
        if (err.response?.status === 404) {
          setError("Tính năng tìm kiếm tin nhắn hiện không khả dụng. Vui lòng liên hệ quản trị viên!");
        } else if (err.response?.status === 403) {
          setError("Bạn không có quyền sử dụng tính năng tìm kiếm tin nhắn!");
        } else {
          setError("Không thể tìm kiếm tin nhắn: " + (err.response?.data?.message || err.message));
        }
      }
    };

    fetchSearchMessages();
  }, [canSearchMessages, searchMessageQuery, token]);

  const handleOpenChat = (userId) => {
    if (!userId || !authUser || !authUser._id) {
      alert("Không thể mở chat. Dữ liệu người dùng không hợp lệ!");
      return;
    }
    navigate(`/chat/${userId}`);
  };

  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  const handleToggleNotifications = async (notificationId, action) => {
    if (!token) return;

    try {
      if (action === "read") {
        const res = await api.put(`/api/users/notifications/${notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 200) {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
          );
          setUnreadNotificationCount((prev) => prev - 1);
        }
      } else if (action === "delete") {
        const res = await api.delete(`/api/users/notifications/${notificationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 200) {
          setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
          setUnreadNotificationCount((prev) => {
            const deletedNotification = notifications.find((n) => n._id === notificationId);
            return deletedNotification && !deletedNotification.read ? prev - 1 : prev;
          });
        }
      }
    } catch (error) {
      console.error(`Lỗi khi ${action} thông báo:`, error);
      setError(`Không thể ${action === "read" ? "đánh dấu đã đọc" : "xóa"} thông báo. Vui lòng thử lại!`);
    }
  };

  // Phân trang cho danh sách người dùng
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const maxPageNumbersToShow = 10;
  const pageGroup = Math.floor((currentPage - 1) / maxPageNumbersToShow);
  const startPage = pageGroup * maxPageNumbersToShow + 1;
  const endPage = Math.min(startPage + maxPageNumbersToShow - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (!authUser) {
    return <p className="text-center text-gray-600">Đang tải thông tin người dùng...</p>;
  }

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <Navbar
        unreadCount={totalUnread}
        unreadNotificationCount={unreadNotificationCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-16">
        <ChatTabs unreadCount={totalUnread} onTabChange={setActiveTab} />
      </div>

      <div className="pt-4 max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">
            Danh sách trò chuyện
          </h2>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Thanh tìm kiếm người dùng */}
        <div className="mb-6">
          <input
            type="text"
            value={searchUserQuery}
            onChange={(e) => setSearchUserQuery(e.target.value)}
            placeholder="Tìm kiếm người dùng..."
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50"
          />
        </div>

        {/* Thanh tìm kiếm tin nhắn (chỉ dành cho vai trò đặc biệt) */}
        {canSearchMessages && (
          <div className="mb-6">
            <input
              type="text"
              value={searchMessageQuery}
              onChange={(e) => setSearchMessageQuery(e.target.value)}
              placeholder="Tìm kiếm tin nhắn..."
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50"
            />
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <p className="text-center text-gray-600 p-6">Đang tải danh sách trò chuyện...</p>
          ) : error ? (
            <p className="text-center text-gray-600 p-6">Có lỗi xảy ra. Vui lòng thử lại sau.</p>
          ) : searchMessageQuery.trim() && canSearchMessages ? (
            <>
              <table className="w-full table-auto">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="p-3 text-left text-gray-600 font-semibold">Người dùng</th>
                    <th className="p-3 text-left text-gray-600 font-semibold">Tin nhắn</th>
                    <th className="p-3 text-left text-gray-600 font-semibold">Ngày</th>
                  </tr>
                </thead>
                <tbody>
                  {searchMessages.length > 0 ? (
                    searchMessages.map((item) => (
                      <tr
                        key={item.message._id}
                        onClick={() => handleOpenChat(item.userId)}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition duration-200"
                      >
                        <td className="p-3">
                          <div className="flex items-center">
                            <div className="relative">
                              <Link to={`/profile/${item.userId}`}>
                                <img
                                  src={getAvatarUrl({ name: item.userName })}
                                  alt={`${item.userName}'s avatar`}
                                  className="w-10 h-10 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                                  onError={(e) => (e.target.src = "https://placehold.co/50")}
                                />
                              </Link>
                            </div>
                            <div className="font-medium text-gray-800">{item.userName}</div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-600 truncate max-w-xs">
                            {item.message.content?.substring(0, 30) || "Không có tin nhắn"}
                            {item.message.content?.length > 30 ? "..." : ""}
                          </span>
                        </td>
                        <td className="p-3 text-gray-500">
                          {item.message.createdAt
                            ? new Date(item.message.createdAt).toLocaleDateString("vi-VN")
                            : "N/A"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center text-gray-600 p-6">
                        Không tìm thấy tin nhắn nào.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : filteredUsers.length > 0 ? (
            <>
              <table className="w-full table-auto">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="p-3 text-left text-gray-600 font-semibold">Người dùng</th>
                    <th className="p-3 text-left text-gray-600 font-semibold">Tin nhắn gần nhất</th>
                    <th className="p-3 text-left text-gray-600 font-semibold">Ngày</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.map((user) => {
                    const userInfo = getUserInfo(user._id);
                    return (
                      <tr
                        key={user._id}
                        onClick={() => handleOpenChat(user._id)}
                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                          unreadCounts[user._id] > 0 ? "bg-indigo-50 font-semibold" : ""
                        } transition duration-200`}
                      >
                        <td className="p-3">
                          <div className="flex items-center">
                            <div className="relative">
                              <Link to={`/profile/${userInfo.membershipId || userInfo._id}`}>
                                <img
                                  src={getAvatarUrl(userInfo)}
                                  alt={`${userInfo.name}'s avatar`}
                                  className="w-10 h-10 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                                  onError={(e) => (e.target.src = "https://placehold.co/50")}
                                />
                              </Link>
                              <span
                                className={`absolute bottom-0 right-2 w-4 h-4 rounded-full border-2 border-white ${
                                  userInfo.isOnline ? "bg-green-400" : "bg-gray-400"
                                }`}
                              />
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{userInfo.name}</div>
                              <div className="text-sm text-gray-500">
                                {userInfo.age || "N/A"} • {userInfo.locationText?.city || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center">
                            {unreadCounts[user._id] > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 mr-2">
                                {unreadCounts[user._id]}
                              </span>
                            )}
                            <span className="text-gray-600 truncate max-w-xs">
                              {user.lastMessageContent?.substring(0, 30) || "Không có tin nhắn"}
                              {user.lastMessageContent?.length > 30 ? "..." : ""}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-gray-500">
                          {user.latestMessageTimestamp
                            ? new Date(user.latestMessageTimestamp).toLocaleDateString("vi-VN")
                            : "N/A"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Phân trang */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center p-4">
                  <button
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 mx-1 rounded-lg ${
                      currentPage === 1 ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"
                    } transition duration-200`}
                  >
                    Trước
                  </button>

                  {totalPages > maxPageNumbersToShow ? (
                    pageNumbers.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-4 py-2 mx-1 rounded-lg ${
                          currentPage === pageNumber ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                        } transition duration-200`}
                      >
                        {pageNumber}
                      </button>
                    ))
                  ) : (
                    Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-4 py-2 mx-1 rounded-lg ${
                          currentPage === pageNumber ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                        } transition duration-200`}
                      >
                        {pageNumber}
                      </button>
                    ))
                  )}

                  <button
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 mx-1 rounded-lg ${
                      currentPage === totalPages ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"
                    } transition duration-200`}
                  >
                    Sau
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-600 p-6">Bạn chưa có cuộc trò chuyện nào.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatList;